import React, { useEffect, useRef } from 'react';

const usePageProtection = () => {
  const overlayRef = useRef(null);
  const intervalRef = useRef(null);

  useEffect(() => {
    // Enhanced keyboard blocking
    const blockKeyboardShortcuts = (e) => {
      // Block F12 (DevTools)
      if (e.keyCode === 123 || e.key === 'F12') {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
      
      // Block Print Screen (44)
      if (e.keyCode === 44) {
        e.preventDefault();
        e.stopPropagation();
        showScreenshotWarning();
        return false;
      }
      
      // Block Alt + Print Screen
      if (e.altKey && e.keyCode === 44) {
        e.preventDefault();
        e.stopPropagation();
        showScreenshotWarning();
        return false;
      }
      
      // Block Windows + Print Screen
      if (e.metaKey && e.keyCode === 44) {
        e.preventDefault();
        e.stopPropagation();
        showScreenshotWarning();
        return false;
      }
      
      // Block Ctrl+Shift+I (DevTools)
      if (e.ctrlKey && e.shiftKey && e.keyCode === 73) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
      
      // Block Ctrl+Shift+J (Console)
      if (e.ctrlKey && e.shiftKey && e.keyCode === 74) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
      
      // Block Ctrl+Shift+C (Element Inspector)
      if (e.ctrlKey && e.shiftKey && e.keyCode === 67) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
      
      // Block Ctrl+U (View Source)
      if (e.ctrlKey && e.keyCode === 85) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
      
      // Block Ctrl+S (Save As)
      if (e.ctrlKey && e.keyCode === 83) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
      
      // Block Ctrl+P (Print)
      if (e.ctrlKey && e.keyCode === 80) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    };

    // Show warning when screenshot attempt is detected
    const showScreenshotWarning = () => {
      if (overlayRef.current) return; // Don't show multiple warnings

      const warningOverlay = document.createElement('div');
      warningOverlay.id = 'screenshot-warning-overlay';
      warningOverlay.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(255, 0, 0, 0.9);
        color: white;
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 999999;
        font-family: Arial, sans-serif;
        font-size: 24px;
        font-weight: bold;
        text-align: center;
      `;
      warningOverlay.innerHTML = `
        <div>
          <h2>⚠️ SCREENSHOT BLOCKED</h2>
          <p>Screenshots are not allowed on this page</p>
          <p style="font-size: 16px;">This warning will disappear in 3 seconds...</p>
        </div>
      `;
      
      document.body.appendChild(warningOverlay);
      overlayRef.current = warningOverlay;

      // Remove warning after 3 seconds
      setTimeout(() => {
        if (overlayRef.current) {
          document.body.removeChild(overlayRef.current);
          overlayRef.current = null;
        }
      }, 3000);
    };

    // Block right-click menu
    const blockRightClick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      return false;
    };

    // Block text selection and drag
    const blockSelection = (e) => {
      e.preventDefault();
      e.stopPropagation();
      return false;
    };

    // Block print functionality
    const blockPrint = (e) => {
      e.preventDefault();
      e.stopPropagation();
      return false;
    };

    // Blur content when page loses focus (potential screenshot tools)
    const handleVisibilityChange = () => {
      if (document.hidden || !document.hasFocus()) {
        document.body.style.filter = 'blur(10px)';
        document.body.style.transition = 'filter 0.1s';
        
        // Add overlay to prevent screenshot tools
        if (!overlayRef.current) {
          const blurOverlay = document.createElement('div');
          blurOverlay.id = 'blur-protection-overlay';
          blurOverlay.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(0, 0, 0, 0.8);
            color: white;
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 999998;
            font-family: Arial, sans-serif;
            font-size: 18px;
            text-align: center;
          `;
          blurOverlay.innerHTML = `
            <div>
              <h3>🔒 CONTENT PROTECTED</h3>
              <p>Please return to this tab to continue</p>
            </div>
          `;
          document.body.appendChild(blurOverlay);
          overlayRef.current = blurOverlay;
        }
      } else {
        setTimeout(() => {
          document.body.style.filter = 'none';
          if (overlayRef.current && overlayRef.current.id === 'blur-protection-overlay') {
            document.body.removeChild(overlayRef.current);
            overlayRef.current = null;
          }
        }, 300);
      }
    };

    // Monitor clipboard for screenshot detection
    const monitorClipboard = async () => {
      try {
        if (navigator.clipboard && navigator.clipboard.read) {
          const clipboardItems = await navigator.clipboard.read();
          for (const clipboardItem of clipboardItems) {
            if (clipboardItem.types.includes('image/png') || 
                clipboardItem.types.includes('image/jpeg') ||
                clipboardItem.types.includes('image/gif')) {
              showScreenshotWarning();
              // Clear the clipboard
              await navigator.clipboard.writeText('');
              break;
            }
          }
        }
      } catch (error) {
        // Silently handle clipboard access errors
      }
    };

    // Add protective CSS styles
    const addProtectiveStyles = () => {
      const styleElement = document.createElement('style');
      styleElement.id = 'content-protection-styles';
      styleElement.textContent = `
        * {
          -webkit-user-select: none !important;
          -moz-user-select: none !important;
          -ms-user-select: none !important;
          user-select: none !important;
          -webkit-touch-callout: none !important;
          -webkit-tap-highlight-color: transparent !important;
          -webkit-user-drag: none !important;
          -khtml-user-drag: none !important;
          -moz-user-drag: none !important;
          -o-user-drag: none !important;
          user-drag: none !important;
        }
        
        /* Specifically block selection on all text elements including labels */
        p, span, div, h1, h2, h3, h4, h5, h6, label, .MuiTypography-root, 
        .MuiFormLabel-root, .MuiInputLabel-root, .form-label, .field-text,
        .MuiListItemText-root, .MuiMenuItem-root, .MuiButton-root,
        [class*="text"], [class*="label"], [class*="title"] {
          -webkit-user-select: none !important;
          -moz-user-select: none !important;
          -ms-user-select: none !important;
          user-select: none !important;
          -webkit-touch-callout: none !important;
          -webkit-user-drag: none !important;
          -khtml-user-drag: none !important;
          -moz-user-drag: none !important;
          -o-user-drag: none !important;
          user-drag: none !important;
        }
        
        /* Allow text selection and input in form fields only - but NOT disabled ones */
        input:not([disabled]), textarea:not([disabled]), [contenteditable], 
        .MuiInputBase-input:not([disabled]), .MuiTextField-root input:not([disabled]),
        .MuiInputBase-inputMultiline:not([disabled]), .MuiOutlinedInput-input:not([disabled]) {
          -webkit-user-select: text !important;
          -moz-user-select: text !important;
          -ms-user-select: text !important;
          user-select: text !important;
          pointer-events: auto !important;
        }
        
        /* Specifically block selection on disabled textareas and inputs */
        textarea[disabled], input[disabled], .QuestionPrompt, .StyledTextField {
          -webkit-user-select: none !important;
          -moz-user-select: none !important;
          -ms-user-select: none !important;
          user-select: none !important;
          -webkit-touch-callout: none !important;
          -webkit-user-drag: none !important;
          -khtml-user-drag: none !important;
          -moz-user-drag: none !important;
          -o-user-drag: none !important;
          user-drag: none !important;
          pointer-events: none !important;
        }
        
        /* Allow interaction with autocomplete and form components */
        .MuiAutocomplete-root, .MuiFormControl-root, .form-control, 
        .EmailInput, .highlighted-text-container, .email-input-wrapper {
          pointer-events: auto !important;
        }
        
        .MuiAutocomplete-root input:not([disabled]), .MuiFormControl-root input:not([disabled]), .form-control input:not([disabled]),
        .EmailInput input:not([disabled]), .highlighted-text-container input:not([disabled]), .email-input-wrapper input:not([disabled]),
        .MuiAutocomplete-root textarea:not([disabled]), .MuiFormControl-root textarea:not([disabled]), .form-control textarea:not([disabled]),
        .EmailInput textarea:not([disabled]), .highlighted-text-container textarea:not([disabled]), .email-input-wrapper textarea:not([disabled]) {
          pointer-events: auto !important;
          -webkit-user-select: text !important;
          -moz-user-select: text !important;
          -ms-user-select: text !important;
          user-select: text !important;
        }
        
        /* Specific styles for email input fields - only if not disabled */
        .email-input-wrapper input:not([disabled]),
        .email-input-wrapper textarea:not([disabled]),
        .email-input-wrapper [contenteditable] {
          pointer-events: auto !important;
          -webkit-user-select: text !important;
          -moz-user-select: text !important;
          -ms-user-select: text !important;
          user-select: text !important;
        }
        
        img, video, canvas, svg {
          -webkit-user-drag: none !important;
          -khtml-user-drag: none !important;
          -moz-user-drag: none !important;
          -o-user-drag: none !important;
          user-drag: none !important;
        }
        
        /* Allow interactive icons to work but prevent selection */
        .cursor-pointer, [role="button"], button {
          pointer-events: auto !important;
          -webkit-user-select: none !important;
          -moz-user-select: none !important;
          -ms-user-select: none !important;
          user-select: none !important;
        }
        
        .cursor-pointer svg, [role="button"] svg, button svg {
          pointer-events: auto !important;
          -webkit-user-select: none !important;
          -moz-user-select: none !important;
          -ms-user-select: none !important;
          user-select: none !important;
        }
        
        /* Force disable selection on common text containers */
        .card-body, .card-header, .modal-body, .modal-header,
        .table, .table td, .table th, .list-group-item,
        .nav-link, .navbar-text, .breadcrumb, .alert {
          -webkit-user-select: none !important;
          -moz-user-select: none !important;
          -ms-user-select: none !important;
          user-select: none !important;
        }
        
        @media print {
          body * { 
            visibility: hidden !important; 
          }
          body::before {
            content: "Printing is not allowed" !important;
            position: fixed !important;
            top: 50% !important;
            left: 50% !important;
            transform: translate(-50%, -50%) !important;
            font-size: 24px !important;
            color: red !important;
            visibility: visible !important;
          }
        }
        
        /* Prevent screenshot tools overlay */
        body::after {
          content: '';
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          pointer-events: none;
          z-index: 999997;
          background: transparent;
        }
      `;
      document.head.appendChild(styleElement);
    };

    // Monitor clipboard periodically
    intervalRef.current = setInterval(monitorClipboard, 1000);

    // Register event listeners with capture phase
    document.addEventListener('keydown', blockKeyboardShortcuts, true);
    document.addEventListener('keyup', blockKeyboardShortcuts, true);
    document.addEventListener('contextmenu', blockRightClick, true);
    document.addEventListener('selectstart', blockSelection, true);
    document.addEventListener('dragstart', blockSelection, true);
    document.addEventListener('beforeprint', blockPrint, true);
    document.addEventListener('visibilitychange', handleVisibilityChange, true);
    window.addEventListener('blur', handleVisibilityChange, true);
    window.addEventListener('focus', handleVisibilityChange, true);
    
    // Apply protective styles
    addProtectiveStyles();

    // Cleanup on unmount
    return () => {
      document.removeEventListener('keydown', blockKeyboardShortcuts, true);
      document.removeEventListener('keyup', blockKeyboardShortcuts, true);
      document.removeEventListener('contextmenu', blockRightClick, true);
      document.removeEventListener('selectstart', blockSelection, true);
      document.removeEventListener('dragstart', blockSelection, true);
      document.removeEventListener('beforeprint', blockPrint, true);
      document.removeEventListener('visibilitychange', handleVisibilityChange, true);
      window.removeEventListener('blur', handleVisibilityChange, true);
      window.removeEventListener('focus', handleVisibilityChange, true);
      
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      
      // Remove protective styles
      const protectiveStyles = document.getElementById('content-protection-styles');
      if (protectiveStyles) {
        protectiveStyles.remove();
      }
      
      // Remove any overlays
      if (overlayRef.current) {
        try {
          document.body.removeChild(overlayRef.current);
        } catch (e) {
          // Handle case where overlay was already removed
        }
        overlayRef.current = null;
      }
      
      // Reset body filter
      document.body.style.filter = 'none';
    };
  }, []);
};

export default usePageProtection;